import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { BulkReassignResultEntity } from '../../domain/entities/bulk-reassign-result.entity';
import { CapacityResultEntity } from '../../domain/entities/capacity-result.entity';
import { RebalancingProposalGroupEntity } from '../../domain/entities/rebalancing-proposal-group.entity';
import { RebalancingProposalMember } from '../../domain/entities/rebalancing-proposal-member.entity';
import { RebalancingProposalEntity } from '../../domain/entities/rebalancing-proposal.entity';
import { TeamBalancingCandidateEntity } from '../../domain/entities/team-balancing-candidate.entity';
import { TeamWorkloadEntity } from '../../domain/entities/team-workload.entity';
import { TeamWorkloadSummaryEntity } from '../../domain/entities/team-workload-summary.entity';
import { TimeOffInfoEntity } from '../../domain/entities/time-off-info.entity';
import { WorkloadInvestigationEntity } from '../../domain/entities/workload-investigation.entity';
import {
  WorkloadDirectorRollupEntity,
  WorkloadManagerCardEntity,
} from '../../domain/entities/workload-director-rollup.entity';
import { WorkloadEligibleAgentEntity } from '../../domain/entities/workload-eligible-agent.entity';
import { WorkloadKpiMemberPageEntity } from '../../domain/entities/workload-kpi-member-page.entity';
import { WorkloadMemberEntity } from '../../domain/entities/workload-member.entity';
import { WorkloadMemberDetailEntity } from '../../domain/entities/workload-member-detail.entity';
import {
  WorkloadTimeOffApprovalDelegationEntity,
  WorkloadTimeOffBlockEntity,
} from '../../domain/entities/workload-time-off-block.entity';
import {
  WorkloadEligibleDelegateEntity,
  WorkloadManagedTeamEntity,
  WorkloadTimeOffPeriodEntity,
} from '../../domain/entities/workload-managed-team.entity';
import { TeamWorkloadApi } from '../api/team-workload.api';
import {
  ApplyRebalanceRequestDto,
  BulkReassignRequestDto,
  BulkReassignResponseDto,
  CapacityUpsertRequestDto,
  CapacityUpsertResponseDto,
  EligibleDelegateResponseDto,
  GenerateRebalanceProposalsRequestDto,
  ManagedTeamResponseDto,
  RebalanceProposalGroupResponseDto,
  RebalanceProposalMemberResponseDto,
  RebalanceProposalResponseDto,
  RebalanceReassignmentItemDto,
  SingleReassignRequestDto,
  TeamBalancingCandidateResponseDto,
  TimeOffBlockRequestDto,
  TimeOffPeriodResponseDto,
  WorkloadDirectorRollupResponseDto,
  WorkloadEligibleAgentResponseDto,
  WorkloadInvestigationRowResponseDto,
  WorkloadKpiMemberPageResponseDto,
  WorkloadMemberDetailResponseDto,
  WorkloadMemberPreviewResponseDto,
  WorkloadOverviewResponseDto,
  WorkloadTeamHierarchyResponseDto,
  WorkloadTeamMemberResponseDto,
  WorkloadTimeOffBlockApprovalDelegationResponseDto,
  WorkloadTimeOffBlockResponseDto,
} from '../dtos/workload-responses.dto';

export interface ApplyRebalancingCommand {
  reassignments: { investigationId: string; fromUserId: string; toUserId: string }[];
}

export interface OverviewBundle {
  summary: TeamWorkloadSummaryEntity;
  overCapacityMembers: WorkloadMemberEntity[];
  overdueMembers: WorkloadMemberEntity[];
  atRiskMembers: WorkloadMemberEntity[];
  timeOffMembers: WorkloadMemberEntity[];
}

@Injectable({ providedIn: 'root' })
export class TeamWorkloadRepository {
  private readonly _api = inject(TeamWorkloadApi);

  public getOverviewBundle(): Observable<OverviewBundle> {
    return this._api.getOverview().pipe(
      map((dto) => ({
        summary: this._mapSummary(dto),
        overCapacityMembers: dto.overCapacityPreviews.map((p) => this._mapMemberFromPreview(p, false)),
        overdueMembers: dto.overdueMemberPreviews.map((p) => this._mapMemberFromPreview(p, false)),
        atRiskMembers: dto.atRiskMemberPreviews.map((p) => this._mapMemberFromPreview(p, false)),
        timeOffMembers: dto.plannedTimeOffPreviews.map((p) => this._mapMemberFromPreview(p, true)),
      })),
    );
  }

  public getTeamWorkloads(includeClosed = false): Observable<TeamWorkloadEntity[]> {
    return this._api.getTeams().pipe(map((teams) => teams.map((t) => this._mapTeam(t, includeClosed))));
  }

  public getMemberDetail(
    userId: string,
    closedPage = 0,
    closedSize = 20,
  ): Observable<WorkloadMemberDetailEntity> {
    return this._api.getMemberDetail(userId, closedPage, closedSize).pipe(map((dto) => this._mapMemberDetail(dto)));
  }

  public getKpiMembers(kpi: string, page = 0, size = 50): Observable<WorkloadKpiMemberPageEntity> {
    return this._api.getKpiMembers(kpi, page, size).pipe(map((dto) => this._mapKpiPage(dto)));
  }

  public getDirectorRollup(): Observable<WorkloadDirectorRollupEntity> {
    return this._api.getDirectorRollup().pipe(map((dto) => this._mapDirectorRollup(dto)));
  }

  public getEligibleAgents(
    investigationIds: string[],
    search?: string,
  ): Observable<WorkloadEligibleAgentEntity[]> {
    return this._api.getEligibleAgents(investigationIds, search).pipe(map((arr) => arr.map((dto) => this._mapEligibleAgent(dto))));
  }

  public getTimeOffTypes(): Observable<{ id: string; name: string }[]> {
    return this._api.getTimeOffTypes().pipe(map((arr) => arr.map((t) => ({ id: t.id, name: t.name }))));
  }

  public upsertCapacity(
    userId: string,
    request: CapacityUpsertRequestDto,
  ): Observable<CapacityResultEntity> {
    return this._api.upsertCapacity(userId, request).pipe(map((dto) => this._mapCapacityResult(dto, userId)));
  }

  private readonly _myManagedTeams$ = this._api
    .getMyManagedTeams()
    .pipe(map((arr) => arr.map((dto) => this._mapManagedTeam(dto))), shareReplay({ bufferSize: 1, refCount: true }));

  public getMyManagedTeams(): Observable<WorkloadManagedTeamEntity[]> {
    return this._myManagedTeams$;
  }

  public getMemberManagedTeams(userId: string): Observable<WorkloadManagedTeamEntity[]> {
    return this._api.getMemberManagedTeams(userId).pipe(map((arr) => arr.map((dto) => this._mapManagedTeam(dto))));
  }

  public addTimeOff(userId: string, request: TimeOffBlockRequestDto): Observable<string> {
    return this._api.addTimeOff(userId, request).pipe(map((res) => res.id));
  }

  public updateTimeOff(id: string, request: TimeOffBlockRequestDto): Observable<void> {
    return this._api.updateTimeOff(id, request);
  }

  public deleteTimeOff(id: string): Observable<void> {
    return this._api.deleteTimeOff(id);
  }

  public singleReassign(investigationId: string, request: SingleReassignRequestDto): Observable<void> {
    return this._api.singleReassign(investigationId, request);
  }

  public bulkReassign(request: BulkReassignRequestDto): Observable<BulkReassignResultEntity> {
    return this._api.bulkReassign(request).pipe(map((dto) => this._mapBulkResult(dto)));
  }

  public getTeamBalancingCandidates(): Observable<TeamBalancingCandidateEntity[]> {
    return this._api
      .getRebalanceCandidates()
      .pipe(map((arr) => arr.map((dto) => mapCandidate(dto))));
  }

  public generateRebalancingProposals(teamIds: string[]): Observable<RebalancingProposalGroupEntity[]> {
    const request: GenerateRebalanceProposalsRequestDto = { teamIds, feedback: [] };
    return this._api
      .generateRebalanceProposals(request)
      .pipe(map((dto) => dto.groups.map((g) => mapProposalGroup(g))));
  }

  public applyRebalancingProposals(cmd: ApplyRebalancingCommand): Observable<number> {
    const request: ApplyRebalanceRequestDto = {
      reassignments: cmd.reassignments.map(
        (r): RebalanceReassignmentItemDto => ({
          investigationId: r.investigationId,
          fromUserId: r.fromUserId,
          toUserId: r.toUserId,
        }),
      ),
    };
    return this._api.applyRebalance(request).pipe(map((dto) => dto.appliedCount));
  }

  public submitRebalancingProposalFeedback(investigationId: string, reason: string, note: string | null): Observable<void> {
    return this._api.submitRebalanceFeedback({ investigationId, reason, note });
  }

  private _mapSummary(dto: WorkloadOverviewResponseDto): TeamWorkloadSummaryEntity {
    const e = new TeamWorkloadSummaryEntity();
    e.overCapacityCount = dto.overCapacityMemberCount;
    e.overdueCasesCount = dto.overdueCaseCount;
    e.atRiskCasesCount = dto.atRiskCaseCount;
    e.plannedTimeOffCount = dto.plannedTimeOffMemberCount;
    return e;
  }

  private _mapMemberFromPreview(
    dto: WorkloadMemberPreviewResponseDto,
    isOnTimeOff = false,
  ): WorkloadMemberEntity {
    const e = new WorkloadMemberEntity();
    e.userId = dto.id;
    e.firstName = dto.firstName;
    e.lastName = dto.lastName;
    e.role = '';
    e.email = dto.email;
    e.phone = '';
    e.teams = [];
    e.activeCasesCount = dto.currentCaseCount;
    e.capacity = dto.effectiveCapacity;
    e.utilizationPercent = this._computeUtilization(dto.currentCaseCount, dto.effectiveCapacity);
    e.overdueCount = 0;
    e.highPriorityCount = 0;
    e.closedThisMonthCount = 0;
    e.isOverCapacity = dto.currentCaseCount > dto.effectiveCapacity;
    e.timeOff = isOnTimeOff ? this._plannedTimeOffPlaceholder() : null;
    e.isOnTimeOff = isOnTimeOff;
    e.investigations = [];
    e.closedHiddenCount = 0;
    e.effectiveCapacity = dto.effectiveCapacity;
    e.currentCaseCount = dto.currentCaseCount;
    return e;
  }

  private _mapMemberFromTeamMember(
    dto: WorkloadTeamMemberResponseDto,
    teamName: string,
    includeClosed = false,
  ): WorkloadMemberEntity {
    const e = new WorkloadMemberEntity();
    e.userId = dto.id;
    e.firstName = dto.firstName;
    e.lastName = dto.lastName;
    e.role = dto.roleName ?? '';
    e.email = dto.email;
    e.phone = '';
    e.teams = [teamName];
    e.activeCasesCount = dto.currentCaseCount;
    e.capacity = dto.effectiveCapacity;
    e.utilizationPercent = this._computeUtilization(dto.currentCaseCount, dto.effectiveCapacity);
    e.overdueCount = dto.overdueCount;
    e.highPriorityCount = 0;
    e.closedThisMonthCount = 0;
    e.isOverCapacity = dto.currentCaseCount > dto.effectiveCapacity;
    e.timeOff = dto.isOnTimeOff ? this._plannedTimeOffPlaceholder() : null;
    const active = dto.activeInvestigations?.map((d) => this._mapInvestigationRow(d)) ?? [];
    const closed = dto.closedInvestigations?.map((d) => this._mapInvestigationRow(d)) ?? [];
    e.investigations = includeClosed ? [...active, ...closed] : active;
    e.closedHiddenCount = includeClosed ? 0 : closed.length;
    e.effectiveCapacity = dto.effectiveCapacity;
    e.currentCaseCount = dto.currentCaseCount;
    e.atRiskCount = dto.atRiskCount;
    e.isOnTimeOff = dto.isOnTimeOff;
    e.isManager = dto.isManager;
    return e;
  }

  private _mapTeam(
    dto: WorkloadTeamHierarchyResponseDto,
    includeClosed = false,
  ): TeamWorkloadEntity {
    const e = new TeamWorkloadEntity();
    e.teamId = dto.id;
    e.teamName = dto.teamName;
    e.managedBy = dto.members
      .filter((m) => m.isManager)
      .map((m) => `${m.firstName} ${m.lastName}`);
    e.overCapacityCount = dto.overCapacityCount;
    e.overdueCount = dto.overdueCount;
    e.onTimeOffCount = dto.onTimeOffCount;
    e.totalMembers = dto.memberCount;
    e.members = dto.members.map((m) => this._mapMemberFromTeamMember(m, dto.teamName, includeClosed));
    return e;
  }

  private _mapInvestigationRow(dto: WorkloadInvestigationRowResponseDto): WorkloadInvestigationEntity {
    const overdueDays = dto.isOverdue ? this._computeOverdueDays(dto.dueDate) : 0;
    const e = new WorkloadInvestigationEntity();
    e.investigationId = dto.id;
    e.investigationNumber = dto.investigationNumber;
    e.investigationTypeName = dto.investigationTypeName;
    e.overdueText = dto.isOverdue ? 'Overdue' : '';
    e.overdueDays = overdueDays;
    e.status = dto.statusName;
    e.detailUrl = `/investigations/${dto.id}`;
    e.investigationTypeId = dto.investigationTypeId;
    e.statusId = dto.statusId;
    e.statusName = dto.statusName;
    e.priorityCode = dto.priorityCode;
    e.dueDate = dto.dueDate;
    e.isOverdue = dto.isOverdue;
    e.isAtRisk = dto.isAtRisk;
    e.isClosed = dto.isClosed;
    return e;
  }

  private _mapTimeOffPeriod(dto: TimeOffPeriodResponseDto): WorkloadTimeOffPeriodEntity {
    const e = new WorkloadTimeOffPeriodEntity();
    e.startDate = dto.startDate;
    e.endDate = dto.endDate;
    return e;
  }

  private _mapEligibleDelegate(dto: EligibleDelegateResponseDto): WorkloadEligibleDelegateEntity {
    const e = new WorkloadEligibleDelegateEntity();
    e.id = dto.id;
    e.userId = dto.userId;
    e.name = dto.name;
    e.role = dto.role;
    e.upcomingTimeOff = dto.upcomingTimeOff.map((t) => this._mapTimeOffPeriod(t));
    return e;
  }

  private _mapManagedTeam(dto: ManagedTeamResponseDto): WorkloadManagedTeamEntity {
    const e = new WorkloadManagedTeamEntity();
    e.id = dto.id;
    e.name = dto.name;
    e.eligibleDelegates = dto.eligibleDelegates.map((d) => this._mapEligibleDelegate(d));
    return e;
  }

  private _mapApprovalDelegation(
    dto: WorkloadTimeOffBlockApprovalDelegationResponseDto,
  ): WorkloadTimeOffApprovalDelegationEntity {
    const e = new WorkloadTimeOffApprovalDelegationEntity();
    e.id = dto.id;
    e.teamId = dto.teamId;
    e.teamName = dto.teamName;
    e.delegatedUserId = dto.delegatedUserId;
    e.delegatedName = dto.delegatedName;
    return e;
  }

  private _mapTimeOffBlock(dto: WorkloadTimeOffBlockResponseDto): WorkloadTimeOffBlockEntity {
    const e = new WorkloadTimeOffBlockEntity();
    e.id = dto.id;
    e.timeOffTypeId = dto.timeOffTypeId;
    e.timeOffTypeName = dto.timeOffTypeName;
    e.startDate = dto.startDate;
    e.endDate = dto.endDate;
    e.notes = dto.notes;
    e.approvalDelegations = (dto.approvalDelegations ?? []).map((d) => this._mapApprovalDelegation(d));
    return e;
  }

  private _mapMemberDetail(dto: WorkloadMemberDetailResponseDto): WorkloadMemberDetailEntity {
    const e = new WorkloadMemberDetailEntity();
    e.userId = dto.id;
    e.firstName = dto.firstName;
    e.lastName = dto.lastName;
    e.email = dto.email;
    e.baseCapacity = dto.baseCapacity;
    e.temporaryCapacity = dto.temporaryCapacity;
    e.temporaryCapacityExpiresOn = dto.temporaryCapacityExpiresOn;
    e.effectiveCapacity = dto.effectiveCapacity;
    e.currentCaseCount = dto.currentCaseCount;
    e.activeInvestigations = dto.activeInvestigations.map((d) => this._mapInvestigationRow(d));
    e.closedInvestigations = dto.closedInvestigations.map((d) => this._mapInvestigationRow(d));
    e.upcomingTimeOff = dto.upcomingTimeOff.map((d) => this._mapTimeOffBlock(d));
    return e;
  }

  private _mapKpiPage(dto: WorkloadKpiMemberPageResponseDto): WorkloadKpiMemberPageEntity {
    const e = new WorkloadKpiMemberPageEntity();
    e.total = dto.total;
    e.page = dto.page;
    e.size = dto.size;
    e.items = dto.items.map((p) => this._mapMemberFromPreview(p, false));
    return e;
  }

  private _mapDirectorRollup(
    dto: WorkloadDirectorRollupResponseDto,
  ): WorkloadDirectorRollupEntity {
    const e = new WorkloadDirectorRollupEntity();
    e.managers = dto.managers.map((m) => {
      const card = new WorkloadManagerCardEntity();
      card.userId = m.id;
      card.firstName = m.firstName;
      card.lastName = m.lastName;
      card.email = m.email;
      card.teamIds = m.teamIds;
      card.teamCount = m.teamCount;
      card.totalMembers = m.totalMembers;
      card.overCapacityCount = m.overCapacityCount;
      card.overdueCount = m.overdueCount;
      card.atRiskCount = m.atRiskCount;
      return card;
    });
    return e;
  }

  private _mapEligibleAgent(dto: WorkloadEligibleAgentResponseDto): WorkloadEligibleAgentEntity {
    const e = new WorkloadEligibleAgentEntity();
    e.userId = dto.id;
    e.firstName = dto.firstName;
    e.lastName = dto.lastName;
    e.email = dto.email;
    e.currentCaseCount = dto.currentCaseCount;
    e.effectiveCapacity = dto.effectiveCapacity;
    return e;
  }

  private _mapCapacityResult(
    dto: CapacityUpsertResponseDto,
    encryptedUserId: string,
  ): CapacityResultEntity {
    const e = new CapacityResultEntity();
    e.userId = encryptedUserId;
    e.baseCapacity = dto.baseCapacity;
    e.temporaryCapacity = dto.temporaryCapacity;
    e.temporaryCapacityExpiresOn = dto.temporaryCapacityExpiresOn;
    e.effectiveCapacity = dto.effectiveCapacity;
    return e;
  }

  private _mapBulkResult(dto: BulkReassignResponseDto): BulkReassignResultEntity {
    const e = new BulkReassignResultEntity();
    e.batchId = dto.batchId;
    e.count = dto.count;
    return e;
  }

  private _computeUtilization(current: number, capacity: number): number {
    if (capacity <= 0) return 0;
    return Math.round((current / capacity) * 100);
  }

  private _plannedTimeOffPlaceholder(): TimeOffInfoEntity {
    const info = new TimeOffInfoEntity();
    info.id = '';
    info.type = '';
    info.startDate = '';
    info.endDate = '';
    info.durationDays = 0;
    info.notes = '';
    return info;
  }

  private _computeOverdueDays(dueDate: string | null): number {
    if (!dueDate) return 0;
    const due = Date.parse(dueDate);
    if (Number.isNaN(due)) return 0;
    const diff = Date.now() - due;
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }
}

export function mapCandidate(dto: TeamBalancingCandidateResponseDto): TeamBalancingCandidateEntity {
  const e = new TeamBalancingCandidateEntity();
  e.teamId = dto.id;
  e.teamName = dto.teamName;
  e.overCapacityCount = dto.overCapacityCount;
  e.overdueCount = dto.overdueCount;
  return e;
}

export function mapProposalMember(dto: RebalanceProposalMemberResponseDto): RebalancingProposalMember {
  const e = new RebalancingProposalMember();
  e.userId = dto.id;
  e.displayName = dto.displayName;
  e.currentCases = dto.currentCases;
  e.capacity = dto.capacity;
  e.utilizationPercent = dto.utilizationPercent;
  return e;
}

export function mapProposal(dto: RebalanceProposalResponseDto): RebalancingProposalEntity {
  const e = new RebalancingProposalEntity();
  e.investigationId = dto.id;
  e.investigationNumber = dto.investigationNumber;
  e.investigationTypeName = dto.investigationTypeName;
  e.investigationStatusLabel = dto.investigationStatusLabel;
  e.isOverdue = dto.isOverdue;
  e.teamId = dto.teamId;
  e.teamName = dto.teamName;
  e.fromMember = mapProposalMember(dto.fromMember);
  e.toMember = mapProposalMember(dto.toMember);
  e.alternates = dto.alternates.map(mapProposalMember);
  e.rationale = dto.rationale;
  return e;
}

export function mapProposalGroup(dto: RebalanceProposalGroupResponseDto): RebalancingProposalGroupEntity {
  const e = new RebalancingProposalGroupEntity();
  e.teamId = dto.teamId;
  e.teamName = dto.teamName;
  e.proposals = dto.proposals.map(mapProposal);
  return e;
}
